import React, {useMemo} from 'react'
import {useSlotRecipe} from '@chakra-ui/react3'
import PropTypes from 'prop-types'
import ModuleContainer from '../../components/module-container'
import NavigationPreviewPanes from './panes'
import PreviewPane from './pane'
import NavigationPreviewToolNav from './tool-nav'
import {PreviewPaneNotices} from './notices'
import {
    deadEndMessage,
    hiddenLabels,
    multipleParentsMessage,
    noLevel1ModuleMessage,
    unmatchedLevel2Message,
    unsupportedLevel3Message,
    unwrap
} from './diagnostics'
import {LEVEL1_ANCHOR, jumpTo, level2Anchor, level3Anchors} from './anchors'
import {previewNavigationRecipe} from './index.recipe'
import FlyoutNavigationLevel2 from '../../components/navigation/flyout/partials/flyout-navigation-level2'
import FlyoutNavigationLevel3, {
    NAVIGATION_LEVEL3
} from '../../components/navigation/flyout/partials/flyout-navigation-level3'
import FlyoutNavigationLevel3Teasers, {
    NAVIGATION_LEVEL3_TEASERS
} from '../../components/navigation/flyout/partials/flyout-navigation-level3-teasers'

const LEVEL3_VIEW_MAP = {
    [NAVIGATION_LEVEL3]: FlyoutNavigationLevel3,
    [NAVIGATION_LEVEL3_TEASERS]: FlyoutNavigationLevel3Teasers
}

const LEVEL3_LABEL_MAP = {
    [NAVIGATION_LEVEL3]: 'L3',
    [NAVIGATION_LEVEL3_TEASERS]: 'L3 teasers'
}

/**
 * Everything a level-3 subtree can hide — rows, "more" list, teaser column and
 * main teaser — in one list.
 *
 * @param {Object} nextLevel - The level-3 subtree.
 * @returns {Array} Its entries, teasers and separators.
 */
const level3HideableItems = ({elements = [], moreElements = [], teaser = [], mainTeaser} = {}) => [
    ...elements,
    ...moreElements,
    ...teaser,
    mainTeaser
]

// the level-2 pane, then every level-3 subtree under it, stacked
const NavigationLevel2 = (props) => {
    const {
        moduleId,
        moduleType,
        pageId,
        level2ModuleId,
        parentTitle,
        previewNoLevel1Module,
        previewParentTitles,
        elements = [],
        teasers = [],
        mainTeaser
    } = props

    const items = useMemo(() => elements.map(unwrap).filter(Boolean), [elements])

    // everything the payload asks for and this pane cannot deliver — a level-3
    // subtree of an unknown type would otherwise go missing without trace
    const issues = useMemo(() => {
        const messages = []
        if (previewNoLevel1Module) {
            messages.push(noLevel1ModuleMessage())
        } else if (!parentTitle) {
            messages.push(unmatchedLevel2Message(level2ModuleId))
        } else if (previewParentTitles?.length > 1) {
            // only once a title did resolve: with none, the breadcrumb is blank
            // for the plainer reason above
            messages.push(multipleParentsMessage(previewParentTitles))
        }
        items.forEach((item) => {
            if (item.type === 'navigationSeparator') return
            if (item.nextLevel && !LEVEL3_VIEW_MAP[item.nextLevel.type]) {
                messages.push(unsupportedLevel3Message(item))
            } else if (!item.nextLevel && !item.link?.value) {
                // an authored-but-empty link is an object with no value, so the
                // target is what decides this and not the field's presence
                messages.push(deadEndMessage(item))
            }
        })
        return messages
    }, [previewNoLevel1Module, previewParentTitles, parentTitle, level2ModuleId, items])

    const hidden = useMemo(
        () => hiddenLabels([...items, ...teasers, mainTeaser]),
        [items, teasers, mainTeaser]
    )

    // handed out over the whole list so a title used twice still gets two ids;
    // the row and the pane it opens both read it by place in `items`
    const level3Ids = useMemo(() => level3Anchors(level2ModuleId, items), [level2ModuleId, items])

    const recipe = useSlotRecipe({recipe: previewNavigationRecipe})
    const styles = useMemo(() => recipe(), [recipe])

    return (
        <ModuleContainer
            moduleId={moduleId}
            moduleType={moduleType}
            pageId={pageId}
            anchor={level2Anchor(level2ModuleId)}
            background="none"
            css={styles.stack}
        >
            <PreviewPane
                trail={[
                    {label: 'L1', anchor: LEVEL1_ANCHOR},
                    {label: 'L2', title: parentTitle}
                ]}
            >
                <PreviewPaneNotices issues={issues} hidden={hidden} />
                <NavigationPreviewPanes reserve="level1">
                    <FlyoutNavigationLevel2
                        module={props}
                        title={parentTitle}
                        onBack={() => jumpTo(LEVEL1_ANCHOR)}
                        onDrillIn={(entry) => jumpTo(level3Ids[items.indexOf(entry)])}
                    />
                </NavigationPreviewPanes>
                <NavigationPreviewToolNav desktopOnly />
            </PreviewPane>

            {items.map((item, idx) => {
                const Level3 = LEVEL3_VIEW_MAP[item.nextLevel?.type]
                if (!Level3) return null

                return (
                    <PreviewPane
                        key={idx}
                        level3
                        id={level3Ids[idx]}
                        trail={[
                            {label: 'L1', anchor: LEVEL1_ANCHOR},
                            {label: 'L2', anchor: level2Anchor(level2ModuleId), title: parentTitle},
                            {label: LEVEL3_LABEL_MAP[item.nextLevel.type], title: item.title}
                        ]}
                    >
                        <PreviewPaneNotices
                            hidden={hiddenLabels(level3HideableItems(item.nextLevel))}
                        />
                        <Level3
                            nextLevel={item.nextLevel}
                            title={item.title}
                            parentTitle={parentTitle}
                            onBack={() => jumpTo(level2Anchor(level2ModuleId))}
                        />
                    </PreviewPane>
                )
            })}
        </ModuleContainer>
    )
}

NavigationLevel2.propTypes = {
    moduleId: PropTypes.string,
    moduleType: PropTypes.string,
    pageId: PropTypes.string,
    level2ModuleId: PropTypes.string,
    parentTitle: PropTypes.string,
    previewNoLevel1Module: PropTypes.bool,
    previewParentTitles: PropTypes.arrayOf(PropTypes.string),
    elements: PropTypes.array,
    teasers: PropTypes.array,
    mainTeaser: PropTypes.object
}

export default NavigationLevel2
